import { z } from "zod";

/**
 * Helpery pro volitelná pole plněná z HTML formulářů.
 *
 * Prázdný `<select>` / `<input>` posílá `""` (ne `undefined`). U striktních
 * validací (`.uuid()`, IČO `.regex()`) by `""` **spadlo validací** a formulář
 * by se nedal odeslat (často i tiše, když pole nemá chybovou hlášku). Tyhle
 * helpery to řeší jednotně napříč celou aplikací:
 *
 *  - prázdné `""` projde validací (= pole je nevyplněné),
 *  - skutečně vyplněná (neprázdná) hodnota se validuje a chybová hláška zůstává,
 *  - na výstupu se `""` mapuje na `undefined` (API dostane `hodnota | undefined`),
 *  - `z.input` zůstává `string | undefined` → formuláře se nerozbijí na typech.
 */

/** Volitelné UUID, typicky z `<select>`. Prázdná volba ("— vyberte —") = nevyplněno. */
export const optionalUuid = (msg = "Neplatné UUID") =>
  z
    .string()
    .optional()
    .refine((v) => !v || z.string().uuid(msg).safeParse(v).success, msg)
    .transform((v) => v || undefined);

/**
 * Volitelné UUID v update kontextu, kde je potřeba rozlišit „beze změny"
 * od „vymazat". `undefined` = pole se nemění, `null` i prázdné `""`
 * (nevybraná volba v `<select>`) = hodnotu smazat.
 */
export const nullableUuid = (msg = "Neplatné UUID") =>
  z
    .string()
    .nullable()
    .optional()
    .refine((v) => !v || z.string().uuid(msg).safeParse(v).success, msg)
    .transform((v) => (v === undefined ? undefined : v || null));

/** Volitelné IČO (přesně 8 číslic) z textového pole. Prázdné = nevyplněno. */
export const optionalIco = (msg = "IČO musí být přesně 8 číslic") =>
  z
    .string()
    .optional()
    .refine((v) => !v || /^\d{8}$/.test(v), msg)
    .transform((v) => v || undefined);

/**
 * Volitelná Kč částka z formulářového inputu. Prázdný input ("") = nevyplněno
 * (undefined) — NIKDY se tiše nekoercuje na 0 (money-path). Desetinnou čárku
 * bere jako tečku. Vyplněná hodnota se validuje na rozsah 0..maxKc.
 */
export const optionalKcFromForm = (maxKc: number, msg = "Částka je mimo povolený rozsah") =>
  z
    .union([z.string().max(30, msg), z.number()])
    .optional()
    .transform((v) => {
      if (v === undefined || typeof v === "number") return v;
      // Trim před testem prázdnosti — Number(" ") by jinak tiše dal 0 (money-path).
      const trimmed = v.trim();
      if (trimmed === "") return undefined;
      return Number(trimmed.replace(",", "."));
    })
    .refine((v) => v === undefined || (Number.isFinite(v) && v >= 0 && v <= maxKc), msg);

/**
 * Kč částka s rozlišením „vymazat" od „beze změny" (update kontext):
 * vynechané pole = undefined (beze změny), prázdné "" i null = null (vymazat),
 * vyplněná hodnota se validuje na rozsah 0..maxKc. V create kontextu vyjde
 * nevyplněné pole jako undefined/null — server obojí ukládá jako NULL.
 */
export const nullableKcFromForm = (maxKc: number, msg = "Částka je mimo povolený rozsah") =>
  z
    .union([z.string().max(30, msg), z.number(), z.null()])
    .optional()
    .transform((v) => {
      if (v === undefined) return undefined;
      if (v === null || typeof v === "number") return v;
      // Trim před testem prázdnosti — whitespace-only = vymazat (ne tiché 0).
      const trimmed = v.trim();
      if (trimmed === "") return null;
      return Number(trimmed.replace(",", "."));
    })
    .refine((v) => v == null || (Number.isFinite(v) && v >= 0 && v <= maxKc), msg);
